"use client";

import { useCallback, useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "./AuthProvider";
import HoverLabel from "./HoverLabel";
import { fetchUnreadCount, UnauthorizedError } from "../lib/api";

/** Window events let the InboxPanel tell the bell to clear/refresh without a
 *  shared store: dispatched on read-all (clear) and on open (re-poll). */
export const INBOX_READ_EVENT = "podlog:inbox-read";
export const INBOX_REFRESH_EVENT = "podlog:inbox-refresh";

/** Cross-tab channel: the tab that fetches a fresh count broadcasts it so other
 *  open tabs update their badge without each firing their own request. */
const BELL_CHANNEL = "podlog:inbox-count";

interface NotificationBellProps {
  /** "rail" matches the desktop/mobile header pill; "floating" is a standalone. */
  variant?: "rail" | "floating";
}

/** Header bell: one button, one destination — the notification feed
 *  (`?panel=inbox`). It used to open a menu offering that plus "Alerts", the
 *  zone/alert-config panel, which made a bell mean two unrelated things: what
 *  has happened, and what you would like to be told about later. Alerts is
 *  configuration and now lives with the other destinations, as a rail tile.
 *
 *  Unread state is a red dot rather than a count, matching FriendsButton beside
 *  it — the exact number is in the feed a click away, and two adjacent icons
 *  should signal "something new" the same way. The count still rides along in
 *  the `aria-label`, where it costs nothing.
 *
 *  The count is seeded from `/api/auth/me` (via the auth context) and refreshed
 *  on demand — on tab focus/visibility, on inbox events, and via a cross-tab
 *  broadcast — rather than a background poll, so an idle signed-in user makes
 *  no recurring calls. Renders nothing for signed-out users. */
export default function NotificationBell({ variant = "rail" }: NotificationBellProps) {
  const { user, initialUnread } = useAuth();
  const [count, setCount] = useState(0);
  const router = useRouter();
  const pathname = usePathname();

  // Seed / re-seed from the auth context (the /me response) — no extra request.
  useEffect(() => {
    setCount(user ? initialUnread : 0);
  }, [user, initialUnread]);

  const refresh = useCallback(async () => {
    if (typeof document !== "undefined" && document.hidden) return;
    try {
      const next = await fetchUnreadCount();
      setCount(next);
      // Share the fresh count with other tabs so they don't re-fetch.
      if (typeof BroadcastChannel !== "undefined") {
        const ch = new BroadcastChannel(BELL_CHANNEL);
        ch.postMessage(next);
        ch.close();
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) setCount(0);
      // Other errors: keep the last known count rather than flicker to 0.
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setCount(0);
      return;
    }
    const onVisible = () => {
      if (!document.hidden) refresh();
    };
    const onRead = () => setCount(0);
    const channel =
      typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(BELL_CHANNEL) : null;
    if (channel) {
      channel.onmessage = (e) => {
        if (typeof e.data === "number") setCount(e.data);
      };
    }
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refresh);
    window.addEventListener(INBOX_READ_EVENT, onRead);
    window.addEventListener(INBOX_REFRESH_EVENT, refresh);
    return () => {
      channel?.close();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refresh);
      window.removeEventListener(INBOX_READ_EVENT, onRead);
      window.removeEventListener(INBOX_REFRESH_EVENT, refresh);
    };
  }, [user, refresh]);

  if (!user) return null;
  if (pathname?.startsWith("/admin")) return null;
  // On the home page the shells render their own rail-variant bell, so the
  // global floating instance from layout.tsx must not double up (as UserMenu).
  if (variant === "floating" && pathname === "/") return null;

  const hasUnread = count > 0;

  return (
    <div className="group relative flex-shrink-0">
      <button
        onClick={() => router.push("/?panel=inbox")}
        aria-label={
          hasUnread
            ? `Notifications (${count} unread)`
            : "Notifications"
        }
        className={`relative flex flex-shrink-0 items-center justify-center rounded-full border border-sky-900/60 text-sky-200 transition-colors hover:bg-white/[0.1] ${
          variant === "rail" ? "h-9 w-9 bg-white/[0.06]" : "h-11 w-11 bg-ocean-900/90 shadow-lg backdrop-blur"
        }`}
      >
        <svg
          viewBox="0 0 24 24"
          className="h-5 w-5 text-sky-200/80"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9a6 6 0 0 1 12 0c0 3.6.8 5.4 1.6 6.4.4.5 0 1.1-.6 1.1H5c-.6 0-1-.6-.6-1.1C5.2 14.4 6 12.6 6 9Z" />
          <path d="M10 20a2 2 0 0 0 4 0" fill="none" />
        </svg>
        {hasUnread && (
          <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-ocean-900" />
        )}
      </button>
      <HoverLabel>Notifications</HoverLabel>
    </div>
  );
}
